import {type Slide, type Slideshow, SlideType} from './video-slideshow.types';
import {AjaxP} from '@mamdev/cad.static-next-lib';

interface VideoSlideshowInitOptions {
  backendUrl?: string;
  initialPage?: number;
  initialSlideIndex?: number;
  initialVideoIDs?: number[];
  isPremiumUser?: boolean;
  isPrototype?: boolean;
  slidesBetweenAds?: number;
}

const consoleLog = (...args: unknown[]) => console.log('[video-slideshow-model]', ...args);

export class VideoSlideshowModel {
  private _backendUrl: URL | undefined;
  private _currentPage = 0;
  private _currentSlideIndex = 0;
  private readonly _initialSlideIndex: number = 0;
  private readonly _initialVideoIDs: number[] = [];
  private readonly _isPremiumUser: boolean = false;
  private readonly _isPrototype: boolean = false;
  private _slides: Slide[] = [];
  private readonly _slidesBetweenAds: number = 0;

  get backendUrl(): URL | undefined {
    return this._backendUrl;
  }

  set backendUrl(url: URL) {
    this._backendUrl = url;
  }

  get currentSlide() {
    return this._slides[this._currentSlideIndex];
  }

  get currentSlideIndex() {
    return this._currentSlideIndex;
  }

  private set currentSlideIndex(index: number) {
    if (index > this._slides.length - 1) index = this._slides.length - 1;
    if (index < 0) index = 0;

    this._currentSlideIndex = index;

    // consoleLog('currentSlideIndex changed', index);
  }

  get initialVideoIDs(): number[] {
    return this._initialVideoIDs;
  }

  get slides() {
    return this._slides;
  }

  get slidesBetweenAds() {
    return this._slidesBetweenAds;
  }

  get totalSlides() {
    return this._slides.length;
  }

  constructor(options?: VideoSlideshowInitOptions) {
    this._currentPage = options?.initialPage ?? 0;
    this._initialSlideIndex = options?.initialSlideIndex ?? 0;
    this._initialVideoIDs = options?.initialVideoIDs ?? [];
    this._isPremiumUser = options?.isPremiumUser ?? false;
    this._isPrototype = options?.isPrototype ?? false;
    this._slidesBetweenAds = options?.slidesBetweenAds ?? 3;
    this._initialVideoIDs = options?.initialVideoIDs ?? [];

    this._currentSlideIndex = this._initialSlideIndex;

    if (options?.backendUrl) {
      this.backendUrl = new URL(options.backendUrl, window.location.origin);

      if (options.initialVideoIDs && !options.isPrototype) {
        this.backendUrl.searchParams.set('coremIDs', options.initialVideoIDs.join(','));
      }
    }
  }

  addSlides(slides: Slide[]) {
    slides.map(slide => {
      slide.type ??= SlideType.video;
      slide.content25watched = false;
      slide.content50watched = false;
      slide.content75watched = false;
      slide.playbackStartedPixelFired = false;

      return slide;
    });

    this._slides = this._slides.concat(slides);

    // Remove duplicate slides by converting the array to a Map {coremId -> slide} to guarantee coremId uniqueness,
    // while preserving insertion order, then converting the map back to an array by taking only the map values, which are the slides.
    this._slides = [...new Map(this._slides.map(slide => [slide.coremId, slide])).values()];
  }

  fetchNextSlides(): Promise<void> {
    consoleLog('fetch slides for page ' + this._currentPage);

    const url = this.generateUrlForCurrentPage();

    consoleLog('URL for fetching next slides', url);

    return AjaxP.getJSON<Slideshow>(url.toString()).then(data => {
      // consoleLog('slide data fetched', this.currentSlideIndex);

      const firstTimeFetch = this._slides.length === 0;

      const newSlides = data.entries.map<Slide>(slide => {
        slide.type = SlideType.video;
        return slide;
      });

      if (newSlides.length < 1) return;

      this.addSlides(newSlides);
      this._currentPage += 1;

      // Insert co-slides and update the initial slide index, unless the user is a premium subscriber.
      if (!this._isPremiumUser) {
        this.insertBreakSlides(firstTimeFetch);
      }
    });
  }

  generateUrlForCurrentPage() {
    const url = this.backendUrl;

    if (!url) {
      throw new Error('Missing endpoint URL');
    }

    if (this._isPrototype) {
      url.pathname = url.pathname.replace('page-0', `page-${this._currentPage}`);
    } else if (this._slides.length === 0 && this._initialVideoIDs.length > 0) {
      // If it's the first time fetching data, the initial video IDs are used instead of the page number.
      url.searchParams.set('coremIDs', this._initialVideoIDs.join(','));
      url.searchParams.delete('pageNumber');
    } else {
      // Set which page number we want to query, for the backend data pagination.
      url.searchParams.set('pageNumber', this._currentPage.toString());
      url.searchParams.delete('coremIDs');
    }

    return url;
  }

  getSlide(index: number) {
    return this._slides[index];
  }

  goToSlide(index: number) {
    this.currentSlideIndex = index;

    const currentSlide = this.currentSlide;

    currentSlide.content25watched = false;
    currentSlide.content50watched = false;
    currentSlide.content75watched = false;
    currentSlide.playbackStartedPixelFired = false;
  }

  insertBreakSlides(updateCurrentSlideIndex = false) {
    consoleLog('insertBreakSlides', this._slidesBetweenAds);

    if (this.slidesBetweenAds < 1) {
      return;
    }

    let i = this._slidesBetweenAds;

    while (this._slides[i]) {
      // Insert a break slides only when the slide index is an exact multiple of slidesBetweenAds
      if (((i + 1) % (this._slidesBetweenAds + 1) === 0)) {
        // Skip the case when the resulting position already contains an ad, for example when fetching new slides from the backend.
        if (this._slides[i].type === SlideType.video) {
          this._slides.splice(i, 0, {
            type: SlideType.break
          } as Slide);
        }
      }
      i += this._slidesBetweenAds + 1;
    }

    if (updateCurrentSlideIndex) {
      // Adjust slide index to match the actual video slide index, since ad slides have been inserted in between.
      this.currentSlideIndex = this._initialSlideIndex + Math.floor(this._initialSlideIndex / this._slidesBetweenAds);
    }
  }

  isBreakSlide(slide: Slide) {
    return slide.type === SlideType.break;
  }

  isPremiumUser() {
    return this._isPremiumUser;
  }

  isPrototype() {
    return this._isPrototype;
  }

  isVideoSlide(slide: Slide) {
    return slide.type === SlideType.video;
  }

  resetData() {
    this._backendUrl = undefined;
    this._currentPage = 0;
    this._currentSlideIndex = 0;
    this._initialVideoIDs.length = 0;
    this._slides = [];
  }
}
